import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import useEventStream from '../hooks/useEventStream';
import { useAuth } from './AuthContext';

const RealtimeContext = createContext(null);

/**
 * Global SSE bridge. Opens one connection per tab; lets pages subscribe to
 * event types without each opening their own EventSource.
 *
 * Usage in a page:
 *   const { onEvent } = useRealtime();
 *   useEffect(() => onEvent(['coin_transfer_request', ...], refreshFn), []);
 */
export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const listenersRef = useRef(new Map()); // id -> {types, handler}
  const nextIdRef = useRef(0);

  const dispatch = useCallback((type, payload) => {
    // eslint-disable-next-line no-console
    // console.debug('[SSE]', type, payload);
    listenersRef.current.forEach(({ types, handler }) => {
      if (!types || types.includes(type) || types.includes('*')) {
        try { handler(type, payload); } catch { /* ignore */ }
      }
    });

    // Default toasts for key events that every page cares about
    if (payload?.message) {
      if (type === 'coin_transfer_request') toast.info('💸 ' + payload.message);
      else if (type === 'coin_transfer_approved') toast.success('✅ ' + payload.message);
      else if (type === 'coin_transfer_rejected') toast.warning('✖ ' + payload.message);
      else if (type === 'coin_transfer_reversed') toast.warning('↩ ' + payload.message);
      else if (type === 'expense_approved') toast.success('✅ ' + payload.message);
      else if (type === 'expense_rejected') toast.warning('✖ ' + payload.message);
      else if (type === 'expense_pending') toast.info('📝 ' + payload.message);
      else if (type === 'fine_issued') toast.error('⚠ ' + payload.message);
      else if (type === 'quick_settle_nudge') toast.info('⚡ ' + payload.message);
      else if (type === 'comment_mention') toast.info('@ ' + (payload.title || 'You were mentioned'), { description: payload.message, duration: 8000 });
    }
  }, []);

  useEventStream(dispatch, !!user);

  const onEvent = useCallback((types, handler) => {
    const id = ++nextIdRef.current;
    listenersRef.current.set(id, { types, handler });
    return () => listenersRef.current.delete(id);
  }, []);

  return (
    <RealtimeContext.Provider value={{ onEvent }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) return { onEvent: () => () => {} }; // no-op when outside provider
  return ctx;
}
